import {
	ChangeDetectionStrategy,
	Component,
	inject,
	input,
	OnInit,
	signal,
	WritableSignal,
} from '@angular/core';
import { INotification } from '@core/notifications/interfaces/notifications.interfaces';
import { Store } from '@ngrx/store';
import { markNotificationAsRead } from '@core/notifications/state/notifications.actions';
import { openWorkOrderWorkspaceFromOutside } from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { openRequestWorkspaceFromOutside } from '@app/pages/cmms/components/request/state/requests.action';
import { openIncidentWorkspaceFromOutside } from '@app/pages/cmms/components/incident/state/incidents.actions';
import { AuRightSidebarService } from '@app/core/services/au-right-sidebar.service';
import { take } from 'rxjs';
import { AuRoundAvatarModule } from '@shared/ui-components';
import { AuPipesModule } from '@app/shared/pipes/au-pipes.module';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { moreNotificationsIsLoading } from '../../state/notifications.selectors';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { CmmsLocationsSectionComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';

@Component({
	selector: 'au-notification-messages',
	templateUrl: './au-notification-messages.component.html',
	styleUrls: ['./au-notification-messages.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		AuPipesModule,
		AuRoundAvatarModule,
		MatIconModule,
		MatListModule,
		MatProgressSpinnerModule,
		NgxTolgeeModule,
		CmmsLocationsSectionComponent,
	],
})
export class AuNotificationMessagesComponent implements OnInit {
	private readonly store$ = inject(Store);
	private readonly sidebarService = inject(AuRightSidebarService);

	filteredNotifications = input<INotification[]>([]);
	openNotification = input<string>('');
	isActive: WritableSignal<string> = signal('');
	marked = signal(false);
	loadingMoreNotifications$ = this.store$.select(moreNotificationsIsLoading);

	ngOnInit() {
		const matchedNotification = this.filteredNotifications().find(
			notification => notification.object.pk === this.openNotification()
		);
		if (matchedNotification) {
			this.messageClicked(matchedNotification);
		}
	}

	messageClicked(notification: INotification) {
		const objectType = notification.object?.type;
		if (!objectType || objectType === 'blank') {
			return;
		}

		this.isActive.set(notification.pk);
		this.marked.set(true);

		this.subscribeToSidebar();
		this.markNotificationAsRead(notification);

		if (objectType === 'work_order') {
			return this.store$.dispatch(
				openWorkOrderWorkspaceFromOutside({ pk: notification.object.pk })
			);
		}

		if (objectType === 'request') {
			return this.store$.dispatch(
				openRequestWorkspaceFromOutside({ pk: notification.object.pk })
			);
		}

		if (objectType === 'incident') {
			return this.store$.dispatch(
				openIncidentWorkspaceFromOutside({ pk: notification.object.pk })
			);
		}
	}

	subscribeToSidebar() {
		this.sidebarService.sidebarIsClosing$.pipe(take(1)).subscribe(() => {
			this.sidebarService.closeAndClearPortal();
			this.isActive.set('');
		});
	}

	isHtml(content: string): boolean {
		const regexForHTML = /^<([A-Za-z][A-Za-z0-9]*)\b[^>]*>/;
		return regexForHTML.test(content);
	}

	private markNotificationAsRead(notification: INotification) {
		if (!notification.isRead) {
			this.store$.dispatch(markNotificationAsRead({ pk: notification.pk }));
		}
	}
}
